import React from "react";
import { useState } from "react";

import { Filter, IndianRupee, Users, Clock } from "lucide-react";

const FilterPanel = ({ filters, onFiltersChange, isOpen, onToggle }) => {
  const updatePriceRange = (index, value) => {
    const newRange = [...filters.priceRange];
    newRange[index] = value;
    onFiltersChange({ ...filters, priceRange: newRange });
  };
  const MIN = 0;
  const MAX = 2000;
  const STEP = 50;

  const minVal = filters.priceRange[0];
  const maxVal = filters.priceRange[1];

  const minPercent = (minVal / MAX) * 100;
  const maxPercent = (maxVal / MAX) * 100;
  const THUMB_SIZE = 20; // px (matches h-5 w-5)

  return (
    <>
      <button
        onClick={onToggle}
        className="mb-4 sm:mb-6 flex items-center gap-1 sm:gap-2 px-3 sm:px-4 py-1.5 sm:py-2 bg-emerald-50 text-emerald-700 rounded-lg sm:rounded-xl hover:bg-emerald-100 transition-colors duration-200 text-xs sm:text-sm"
      >
        <Filter className="h-3 w-3 sm:h-4 sm:w-4" />
        Filters
      </button>

      <div
        className={`bg-white rounded-lg sm:rounded-2xl shadow-md sm:shadow-lg p-4 sm:p-6 mb-6 sm:mb-8 transition-all duration-300 ${
          isOpen
            ? "opacity-100 max-h-screen"
            : "opacity-0 max-h-0 overflow-hidden"
        }`}
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6">
          <div className="w-full max-w-md rounded-md">
            <div className="flex items-center gap-1 sm:gap-2 mb-2 sm:mb-3">
              <IndianRupee className="h-3 w-3 sm:h-4 sm:w-4 text-emerald-600" />
              <h3 className="font-semibold text-gray-800 text-xs sm:text-sm">
                Price Range
              </h3>
            </div>

            {/* Slider */}
            <div className="relative h-10">
              {/* Base track */}
              <div className="absolute top-1/2 -translate-y-1/2 w-full h-2 bg-gray-300 rounded-full" />

              {/* Active range */}
              <div
                className="absolute top-1/2 -translate-y-1/2 h-2 bg-gradient-to-r from-emerald-500 to-teal-600 rounded-full"
                style={{
                  left: `${minPercent}%`,
                  width: `${maxPercent - minPercent}%`,
                }}
              />
              {/* Min bubble */}
              <div
                className="absolute -top-4 z-20 text-xs bg-gradient-to-r from-emerald-500 to-teal-600 text-white px-2 py-1 rounded-md
  whitespace-nowrap -translate-x-1/2"
                style={{
                  left: `calc(${minPercent}% + ${
                    (50 - minPercent) * (THUMB_SIZE / 100)
                  }px)`,
                }}
              >
                ₹{minVal}
              </div>

              {/* Max bubble */}
              <div
                className="absolute -top-4 z-20 text-xs bg-gradient-to-r from-emerald-500 to-teal-600 text-white px-2 py-1 rounded-md
  whitespace-nowrap -translate-x-1/2"
                style={{
                  left: `calc(${maxPercent}% + ${
                    (50 - maxPercent) * (THUMB_SIZE / 100)
                  }px)`,
                }}
              >
                ₹{maxVal}
              </div>

              {/* Min thumb */}
              <input
                type="range"
                min={MIN}
                max={MAX}
                step={STEP}
                value={minVal}
                onChange={(e) =>
                  updatePriceRange(
                    0,
                    Math.min(Number(e.target.value), maxVal - STEP)
                  )
                }
                className="absolute w-full appearance-none bg-transparent pointer-events-none
  [&::-webkit-slider-thumb]:pointer-events-auto
  [&::-webkit-slider-thumb]:appearance-none
  [&::-webkit-slider-thumb]:h-5
  [&::-webkit-slider-thumb]:w-5
  [&::-webkit-slider-thumb]:rounded-full
  [&::-webkit-slider-thumb]:bg-gradient-to-r from-emerald-500 to-teal-600
  [&::-webkit-slider-thumb]:border-2
  [&::-webkit-slider-thumb]:border-white
  [&::-webkit-slider-thumb]:shadow-md
  [&::-webkit-slider-thumb]:relative
  [&::-webkit-slider-thumb]:top-4
  [&::-webkit-slider-thumb]:-translate-y-1/2"
              />

              {/* Max thumb */}
              <input
                type="range"
                min={MIN}
                max={MAX}
                step={STEP}
                value={maxVal}
                onChange={(e) =>
                  updatePriceRange(
                    1,
                    Math.max(Number(e.target.value), minVal + STEP)
                  )
                }
                className="absolute w-full appearance-none bg-transparent pointer-events-none
  [&::-webkit-slider-thumb]:pointer-events-auto
  [&::-webkit-slider-thumb]:appearance-none
  [&::-webkit-slider-thumb]:h-5
  [&::-webkit-slider-thumb]:w-5
  [&::-webkit-slider-thumb]:rounded-full
  [&::-webkit-slider-thumb]:bg-gradient-to-r from-emerald-500 to-teal-600
  [&::-webkit-slider-thumb]:border-2
  [&::-webkit-slider-thumb]:border-white
  [&::-webkit-slider-thumb]:shadow-md
  [&::-webkit-slider-thumb]:relative
  [&::-webkit-slider-thumb]:top-4
  [&::-webkit-slider-thumb]:-translate-y-1/2"
              />
            </div>

            {/* Footer */}
            <div className="flex justify-between text-xs text-gray-600 mt-2">
              <span>₹{MIN}</span>
              <span>₹{MAX}</span>
            </div>
          </div>

          {/* Available Seats */}
          <div>
            <div className="flex items-center gap-1 sm:gap-2 mb-2 sm:mb-3">
              <Users className="h-3 w-3 sm:h-4 sm:w-4 text-emerald-600" />
              <h3 className="font-semibold text-gray-800 text-xs sm:text-sm">
                Min Seats
              </h3>
            </div>
            <select
              value={filters.minSeats}
              onChange={(e) =>
                onFiltersChange({
                  ...filters,
                  minSeats: parseInt(e.target.value),
                })
              }
              className="w-full px-2 sm:px-3 py-1.5 sm:py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 text-xs sm:text-sm"
            >
              <option value={1}>1+ seat</option>
              <option value={2}>2+ seats</option>
              <option value={3}>3+ seats</option>
              <option value={4}>4+ seats</option>
              <option value={5}>5+ seats</option>
            </select>
          </div>
          {/* Sort By */}
          <div>
            <div className="flex items-center gap-1 sm:gap-2 mb-2 sm:mb-3">
              <Clock className="h-3 w-3 sm:h-4 sm:w-4 text-emerald-600" />
              <h3 className="font-semibold text-gray-800 text-xs sm:text-sm">
                Sort By
              </h3>
            </div>
            <select
              value={filters.sortBy}
              onChange={(e) =>
                onFiltersChange({ ...filters, sortBy: e.target.value })
              }
              className="w-full px-2 sm:px-3 py-1.5 sm:py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 text-xs sm:text-sm"
            >
              <option value="price">Lowest Price</option>
              <option value="time">Earliest Time</option>
              <option value="availability">Most Seats</option>
            </select>
          </div>
        </div>
      </div>
    </>
  );
};

export default FilterPanel;
